import {
    generateLorryList,
    getAllowedVehicleList,
    RemoteVehicleInfo,
    VehicleInfo
} from "@vahak/core/dist/constants/lorryTypes";

export interface NewUserRestrictionGate {
    is_enabled?: boolean;
    from_date?: string;
    to_date?: string;
    exempt_company_ids?: number[];
}

export interface NewUserRestrictionMeta extends NewUserRestrictionGate {
    allowed_capacities_mt?: number[];
    allowed_feet_min?: number;
    allowed_feet_max?: number;
    allowed_capacity_min_mt?: number;
    allowed_capacity_max_mt?: number;
    config?: { vehicles?: RemoteVehicleInfo[] };
    lorry?: NewUserRestrictionGate;
}

export type NewUserRestrictionScope = "load" | "lorry";

/** Firebase remote-config keys that own the vehicle catalogs per posting surface. */
export const VEHICLE_REMOTE_CONFIG_KEY: Record<NewUserRestrictionScope, string> = {
    load: "lorry_config_data",
    lorry: "vehicle_config_data"
};

export const NEW_USER_VEHICLE_RESTRICTION_KEY = "new_user_vehicle_restriction";

function parseISODate(dateStr: string) {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
}

function parseDate(dateStr: string) {
    const [day, month, year] = dateStr.split("/").map(Number);
    return new Date(year, month - 1, day);
}

function isValidDate(date: Date) {
    return !Number.isNaN(date.getTime());
}

function isJoinedOnInExperimentWindow(gate: NewUserRestrictionGate, joinedOn: string) {
    if (!joinedOn) {
        return false;
    }

    const startDate = parseDate(gate.from_date || "");
    const endDate = parseDate(gate.to_date || "");
    const joinedOnDate = parseISODate(joinedOn);

    return (
        isValidDate(startDate) &&
        isValidDate(endDate) &&
        isValidDate(joinedOnDate) &&
        startDate <= joinedOnDate &&
        joinedOnDate < endDate
    );
}

function isValidJoinedOn(joinedOn: string) {
    if (!joinedOn) {
        return false;
    }

    return isValidDate(parseISODate(joinedOn));
}

function getRestrictionGate(meta: NewUserRestrictionMeta, scope: NewUserRestrictionScope): NewUserRestrictionGate {
    if (scope === "lorry") {
        return meta.lorry ?? {};
    }

    return meta;
}

/** Logged-out users and new users (joined within experiment window) get the restriction. */
function shouldApplyRestriction(
    meta: NewUserRestrictionMeta,
    companyId: number,
    joinedOn: string,
    scope: NewUserRestrictionScope
) {
    const gate = getRestrictionGate(meta, scope);

    if (!gate?.is_enabled) {
        return false;
    }

    if (companyId && gate.exempt_company_ids?.includes(companyId)) {
        return false;
    }

    // Logged out, or joinedOn missing/invalid — apply restriction
    if (!companyId || !isValidJoinedOn(joinedOn)) {
        return true;
    }

    // New user within experiment window
    return isJoinedOnInExperimentWindow(gate, joinedOn);
}

function isFeetSizeAllowed(size: number, meta: NewUserRestrictionMeta) {
    if (meta.allowed_feet_min != null && size < meta.allowed_feet_min) {
        return false;
    }
    if (meta.allowed_feet_max != null && size > meta.allowed_feet_max) {
        return false;
    }
    return true;
}

/** Intersect source range with allowed capacity bounds; null if no overlap. */
function clampCapacityRange(
    min: number,
    max: number,
    meta: NewUserRestrictionMeta
): { min: number; max: number } | null {
    const allowedMin = meta.allowed_capacity_min_mt;
    const allowedMax = meta.allowed_capacity_max_mt;

    if (allowedMin == null && allowedMax == null) {
        return { min, max };
    }

    const clampedMin = allowedMin != null ? Math.max(min, allowedMin) : min;
    const clampedMax = allowedMax != null ? Math.min(max, allowedMax) : max;

    if (clampedMin > clampedMax) {
        return null;
    }

    return { min: clampedMin, max: clampedMax };
}

function filterVehicleByRestrictions(
    vehicle: RemoteVehicleInfo,
    meta: NewUserRestrictionMeta
): RemoteVehicleInfo | null {
    // Feet bounds (allowed_feet_min/max) apply to LCV only; others use capacity clamp alone.
    const shouldFilterFeet = vehicle.name?.toLowerCase() === "lcv";

    const feet = shouldFilterFeet ? vehicle.feet?.filter((item) => isFeetSizeAllowed(item.size, meta)) : vehicle.feet;

    const feet_capacity = vehicle.feet_capacity?.flatMap((item) => {
        if (shouldFilterFeet && !isFeetSizeAllowed(item.size, meta)) {
            return [];
        }
        const clamped = clampCapacityRange(item.min, item.max, meta);
        return clamped ? [{ ...item, ...clamped }] : [];
    });

    const tyre_capacity = vehicle.tyre_capacity?.flatMap((item) => {
        const clamped = clampCapacityRange(item.min, item.max, meta);
        return clamped ? [{ ...item, ...clamped }] : [];
    });

    const hadCapacityData = Boolean(vehicle.tyre_capacity?.length || vehicle.feet_capacity?.length);
    const hasCapacityInRange = Boolean(tyre_capacity?.length || feet_capacity?.length);

    // Drop vehicles whose capacities do not overlap the allowed range.
    if (hadCapacityData && !hasCapacityInRange) {
        return null;
    }

    const feet_tyre = shouldFilterFeet
        ? vehicle.feet_tyre
              ?.map((item) => ({
                  ...item,
                  feet: item.feet?.filter((foot) => isFeetSizeAllowed(foot.size, meta)) ?? []
              }))
              .filter((item) => item.feet.length > 0)
        : vehicle.feet_tyre;

    const shouldSyncTyres = Boolean(vehicle.tyre_capacity?.length || vehicle.feet_tyre?.length);
    const allowedTyreCounts = new Set([
        ...(tyre_capacity?.map((item) => item.tyres_count) ?? []),
        ...(feet_tyre?.map((item) => item.tyres_count) ?? [])
    ]);
    const tyres = shouldSyncTyres ? vehicle.tyres?.filter((item) => allowedTyreCounts.has(item.count)) : vehicle.tyres;

    return {
        ...vehicle,
        ...(feet !== undefined ? { feet } : {}),
        ...(feet_capacity !== undefined ? { feet_capacity } : {}),
        ...(tyre_capacity !== undefined ? { tyre_capacity } : {}),
        ...(feet_tyre !== undefined ? { feet_tyre } : {}),
        ...(tyres !== undefined ? { tyres } : {})
    };
}

export const filterExpirementLorryList = (data: {
    list: RemoteVehicleInfo[];
    meta: NewUserRestrictionMeta;
    companyId: number;
    joinedOn: string;
    scope: NewUserRestrictionScope;
}) => {
    const { companyId, meta, joinedOn, scope } = data;
    const fullList = [...data.list];

    if (!shouldApplyRestriction(meta, companyId, joinedOn, scope)) {
        return fullList;
    }

    // Prefer experiment vehicle defs from meta.config; fall back to full catalog.
    const sourceList = meta.config?.vehicles?.length ? meta.config.vehicles : fullList;

    return sourceList
        .map((vehicle) => filterVehicleByRestrictions(vehicle, meta))
        .filter((vehicle): vehicle is RemoteVehicleInfo => Boolean(vehicle));
};

export interface RestrictedVehicleFormMeta {
    allowedCapacityMin: number | undefined;
    allowedCapacityMax: number | undefined;
    allowedVehicleList: VehicleInfo[];
}

/**
 * Filter remote vehicle catalog for a company/scope, then derive form capacity bounds
 * + allowed vehicle chips (used by admin/CS load & lorry posting forms).
 */
export function buildRestrictedVehicleFormMeta(data: {
    list: RemoteVehicleInfo[];
    meta: NewUserRestrictionMeta;
    companyId: number;
    joinedOn: string;
    scope: NewUserRestrictionScope;
}): RestrictedVehicleFormMeta {
    const resultList = filterExpirementLorryList(data) || [];
    const generated = generateLorryList(resultList, resultList);
    const listKey = data.scope === "load" ? "LOAD_POST" : "LORRY_POST";

    return {
        allowedCapacityMin: generated.ALLOWED_CAPACITY?.[listKey]?.min,
        allowedCapacityMax: generated.ALLOWED_CAPACITY?.[listKey]?.max,
        allowedVehicleList: getAllowedVehicleList(data.scope, { srcList: generated.LIST[listKey] })
    };
}
